import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Dumbbell, Video, Loader2, Filter, X, Upload, Play, Trash, CheckCircle, RefreshCw, Shield } from 'lucide-react';
import { needsConversion, convertToMp4 } from '../../utils/videoConverter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Muscle group configuration with colors - Actualizado para usar musculo_principal
const MUSCLE_GROUPS = [
  { key: 'all', label: 'Todos', color: 'bg-primary', dbKey: null },
  { key: 'PECHO', label: 'Pecho', color: 'bg-red-500' },
  { key: 'ESPALDA', label: 'Espalda', color: 'bg-blue-500' },
  { key: 'HOMBROS', label: 'Hombros', color: 'bg-orange-500' },
  { key: 'BÍCEPS', label: 'Bíceps', color: 'bg-green-500' },
  { key: 'TRÍCEPS', label: 'Tríceps', color: 'bg-purple-500' },
  { key: 'CUÁDRICEPS', label: 'Cuádriceps', color: 'bg-yellow-500' },
  { key: 'ISQUIOTIBIALES', label: 'Isquios', color: 'bg-amber-600' },
  { key: 'GLÚTEOS', label: 'Glúteos', color: 'bg-pink-500' },
  { key: 'ADUCTORES', label: 'Aductores', color: 'bg-rose-500' },
  { key: 'ABDUCTORES', label: 'Abductores', color: 'bg-fuchsia-500' },
  { key: 'GEMELOS', label: 'Gemelos', color: 'bg-rose-400' },
  { key: 'CORE', label: 'Core', color: 'bg-cyan-500' },
  { key: 'ANTEBRAZOS', label: 'Antebrazos', color: 'bg-indigo-500' },
  { key: 'SUSPENSIÓN', label: 'TRX/Suspensión', color: 'bg-teal-500' },
  { key: 'PROPIOCEPCIÓN', label: 'Propiocepción', color: 'bg-emerald-500' },
  { key: 'YOGA', label: 'Yoga', color: 'bg-violet-500' },
  { key: 'OTROS', label: 'Otros', color: 'bg-gray-500' },
];

const ExercisesPage = () => {
  const { t } = useLanguage();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', video_url: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // Video upload states
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoFile, setVideoFile] = useState(null);
  const [convertingVideo, setConvertingVideo] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Video player modal
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoToPlay, setVideoToPlay] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  
  // Migration state
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises`);
      setExercises(response.data);
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mapeo de la nueva categoria oficial (Library v2) → grupos visuales existentes
  const CATEGORIA_TO_GROUP = {
    'Empuje horizontal': 'PECHO',
    'Empuje vertical': 'HOMBROS',
    'Traccion horizontal': 'ESPALDA',
    'Traccion vertical': 'ESPALDA',
    'Escapula': 'HOMBROS',
    'Rodilla': 'CUÁDRICEPS',
    'Cadera': 'GLÚTEOS',
    'Brazos': 'BÍCEPS',
    'Gemelo': 'GEMELOS',
    'Core': 'CORE',
    'Estabilidad': 'PROPIOCEPCIÓN',
    'Movilidad': 'PROPIOCEPCIÓN',
    'Cardio': 'OTROS',
    'Gluteo': 'GLÚTEOS',
  };

  // Categorize exercise by muscle group - prioriza categoria nuevo, fallback a musculo_principal
  const getMuscleGroup = (exercise) => {
    let dbGroup = exercise.musculo_principal || exercise.muscle_group || '';
    // Si el ejercicio es de la librería oficial v2, usar la categoria mapeada
    if (exercise.categoria && CATEGORIA_TO_GROUP[exercise.categoria]) {
      dbGroup = CATEGORIA_TO_GROUP[exercise.categoria];
    }
    if (!dbGroup) dbGroup = 'OTROS';
    const found = MUSCLE_GROUPS.find(g => g.key === dbGroup);
    return found || { key: 'OTROS', label: 'Otros', color: 'bg-gray-500' };
  };

  // Filter exercises by search and muscle group
  const filteredExercises = useMemo(() => {
    return exercises.filter(exercise => {
      const groupKey = getMuscleGroup(exercise).key;
      const matchesSearch = search === '' || 
        exercise.name.toLowerCase().includes(search.toLowerCase()) ||
        (exercise.description || '').toLowerCase().includes(search.toLowerCase()) ||
        (exercise.nota_clave || '').toLowerCase().includes(search.toLowerCase()) ||
        (exercise.exercise_id || '').toLowerCase().includes(search.toLowerCase()) ||
        groupKey.toLowerCase().includes(search.toLowerCase());
      
      if (selectedGroup === 'all') {
        return matchesSearch;
      }
      
      return matchesSearch && groupKey === selectedGroup;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises, search, selectedGroup]);

  // Count exercises per group
  const groupCounts = useMemo(() => {
    const counts = { all: exercises.length };
    MUSCLE_GROUPS.forEach(group => {
      if (group.key !== 'all') {
        counts[group.key] = exercises.filter(ex => getMuscleGroup(ex).key === group.key).length;
      }
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (selectedExercise) {
        await axios.put(`${API}/exercises/${selectedExercise.id}`, formData);
        toast.success(t('exerciseUpdated'));
      } else {
        await axios.post(`${API}/exercises`, formData);
        toast.success(t('exerciseCreated'));
      }
      setDialogOpen(false);
      fetchExercises();
      resetForm();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/exercises/${selectedExercise.id}`);
      toast.success(t('exerciseDeleted'));
      setDeleteDialogOpen(false);
      fetchExercises();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const openEditDialog = (exercise) => {
    setSelectedExercise(exercise);
    setFormData({
      name: exercise.name,
      description: exercise.description || '',
      video_url: exercise.video_url || '',
      notes: exercise.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedExercise(null);
    setFormData({ name: '', description: '', video_url: '', notes: '' });
    setVideoFile(null);
    setUploadProgress(0);
    setConversionProgress(0);
    setConvertingVideo(false);
  };

  // Video upload functions
  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Aceptar cualquier tipo de video - la conversión se hace en el backend
      const isVideo = file.type.startsWith('video/') || 
        ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v', '.3gp', '.3gpp', '.hevc', '.mpeg'].some(
          ext => file.name.toLowerCase().endsWith(ext)
        );
      
      if (!isVideo) {
        toast.error('Selecciona un archivo de video');
        return;
      }
      if (file.size > 200 * 1024 * 1024) {
        toast.error('El video es demasiado grande. M\u00e1ximo 200MB');
        return;
      }
      setVideoFile(file);
      toast.success(`Video seleccionado: ${file.name}`);
    }
  };

  const uploadVideo = async (exerciseId) => {
    if (!videoFile) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sesi\u00f3n expirada. Por favor, inicia sesi\u00f3n de nuevo.');
      return;
    }

    // Paso 1: Detectar HEVC/MOV y convertir client-side si es necesario
    let fileToUpload = videoFile;
    try {
      const mustConvert = await needsConversion(videoFile);
      if (mustConvert) {
        setConvertingVideo(true);
        setConversionProgress(0);
        toast.info('Convirtiendo video a formato web (H.264)... puede tardar unos segundos.', { duration: 4000 });
        fileToUpload = await convertToMp4(
          videoFile,
          (p) => setConversionProgress(p),
          (msg) => console.log('[ffmpeg]', msg)
        );
        setConvertingVideo(false);
        setConversionProgress(100);
        toast.success(`Conversi\u00f3n completada (${(fileToUpload.size / 1024 / 1024).toFixed(1)}MB)`);
      }
    } catch (convErr) {
      console.error('Client-side conversion failed:', convErr);
      setConvertingVideo(false);
      // Fallback: subir el original y dejar que el backend intente convertir
      toast.warning('No se pudo convertir en el navegador. Subiendo original...');
      fileToUpload = videoFile;
    }
    
    setUploadingVideo(true);
    setUploadProgress(0);
    
    // Subir el archivo (directo si <10MB, chunked si mayor para evitar proxy limits)
    const useChunked = fileToUpload.size > 10 * 1024 * 1024;
    
    try {
      if (useChunked) {
        await uploadVideoChunked(exerciseId, token, fileToUpload);
      } else {
        const formDataUpload = new FormData();
        formDataUpload.append('file', fileToUpload);
        const response = await axios.post(`${API}/exercises/${exerciseId}/upload-video`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          timeout: 300000,
          maxContentLength: 250 * 1024 * 1024,
          maxBodyLength: 250 * 1024 * 1024,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
        toast.success(`Video subido: ${response.data.file_size_mb || '?'}MB`);
        setVideoFile(null);
        fetchExercises();
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      // Si falla directo, intentar chunked como fallback
      if (!useChunked && (error.message?.includes('Network') || error.response?.status === 413)) {
        toast.info('Subiendo en partes...');
        try {
          await uploadVideoChunked(exerciseId, token, fileToUpload);
        } catch (chunkError) {
          const errorMsg = chunkError.response?.data?.detail || chunkError.message || 'Error al subir el video';
          toast.error(errorMsg);
        }
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Error al subir el video';
        toast.error(errorMsg);
      }
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      setConversionProgress(0);
    }
  };
  
  const uploadVideoChunked = async (exerciseId, token, fileOverride = null) => {
    const file = fileOverride || videoFile;
    const CHUNK_SIZE = 1.5 * 1024 * 1024; // 1.5MB para evitar limites de proxy
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      const fd = new FormData();
      fd.append('chunk', chunk);
      fd.append('chunk_index', i.toString());
      fd.append('total_chunks', totalChunks.toString());
      fd.append('upload_id', uploadId);
      fd.append('filename', file.name);
      
      await axios.post(`${API}/exercises/${exerciseId}/upload-video-chunk`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
        timeout: 60000
      });
      
      setUploadProgress(Math.round(((i + 1) / totalChunks) * 90));
    }
    
    const response = await axios.post(`${API}/exercises/${exerciseId}/finalize-video-upload`, {
      upload_id: uploadId, filename: file.name, total_chunks: totalChunks
    }, {
      headers: { 'Authorization': `Bearer ${token}` },
      timeout: 180000
    });
    
    setUploadProgress(100);
    toast.success(`Video subido: ${response.data.file_size_mb || '?'}MB`);
    setVideoFile(null);
    fetchExercises();
  };

  const deleteVideo = async (exerciseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/exercises/${exerciseId}/video`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Video eliminado');
      fetchExercises();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al eliminar el video';
      toast.error(errorMsg);
    }
  };

  const handleSubmitWithVideo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let exerciseId;
      if (selectedExercise) {
        await axios.put(`${API}/exercises/${selectedExercise.id}`, formData);
        exerciseId = selectedExercise.id;
        toast.success(t('exerciseUpdated'));
      } else {
        const response = await axios.post(`${API}/exercises`, formData);
        exerciseId = response.data.id;
        toast.success(t('exerciseCreated'));
      }
      
      // Si hay video pendiente, subirlo
      if (videoFile) {
        await uploadVideo(exerciseId);
      }
      
      setDialogOpen(false);
      fetchExercises();
      resetForm();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Check if exercise has local video
  const hasLocalVideo = (exercise) => {
    return exercise.video_url && exercise.video_url.startsWith('/api/videos/');
  };

  // Get video URL with auth
  const getVideoUrl = (exercise) => {
    if (hasLocalVideo(exercise)) {
      return `${process.env.REACT_APP_BACKEND_URL}${exercise.video_url}`;
    }
    return exercise.video_url;
  };

  // Play video with authentication - using direct URL with token
  const playVideo = async (exercise) => {
    if (!hasLocalVideo(exercise)) {
      window.open(exercise.video_url, '_blank');
      return;
    }
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sesión expirada. Por favor, inicia sesión de nuevo.');
      return;
    }
    
    setVideoToPlay(exercise);
    setVideoModalOpen(true);
    setLoadingVideo(false);
    
    // Build the video URL with token as query parameter for streaming
    const videoUrl = `${process.env.REACT_APP_BACKEND_URL}${exercise.video_url}?token=${token}`;
    console.log('Video streaming URL:', videoUrl);
    
    // Set the URL directly (no blob needed with streaming)
    setVideoBlob(videoUrl);
  };

  // Cleanup when modal closes
  const closeVideoModal = () => {
    setVideoBlob(null);
    setVideoToPlay(null);
    setVideoModalOpen(false);
  };

  // Migrate exercise library - marks old exercises as legacy and imports new ones
  const migrateExerciseLibrary = async () => {
    const confirmMsg = `⚠️ MIGRACIÓN DE BIBLIOTECA DE EJERCICIOS

Esta acción:
1. Marcará los ${exercises.length} ejercicios actuales como "legacy" (seguirán funcionando en planes existentes pero no aparecerán en el catálogo)
2. Importará 202 ejercicios nuevos "blindados" con información completa

¿Continuar con la migración?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setMigrating(true);
      const response = await axios.post(`${API}/admin/migrate-exercises-legacy`);
      toast.success(response.data.message);
      
      // Show details in a modal or alert
      const details = response.data.detalles;
      alert(`✅ Migración completada:
      
• Ejercicios previos: ${details.ejercicios_previos}
• Marcados como legacy: ${details.marcados_como_legacy}
• Nuevos insertados: ${details.nuevos_insertados}
• Actualizados: ${details.actualizados}
• Biblioteca activa: ${details.biblioteca_activa}`);
      
      fetchExercises();
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(error.response?.data?.detail || 'Error en la migración');
    } finally {
      setMigrating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="exercises-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('exercises')}</h1>
            <p className="text-muted-foreground">{filteredExercises.length} de {exercises.length} {t('exercises').toLowerCase()}</p>
          </div>
          <div className="flex gap-2">
            {/* Botón Migrar Biblioteca - Solo visible para admin */}
            <Button 
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              onClick={migrateExerciseLibrary}
              disabled={migrating}
              title="Migración segura: marca ejercicios viejos como legacy e importa 202 nuevos"
              data-testid="migrate-library-button"
            >
              {migrating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Shield className="h-4 w-4 mr-2" />
              )}
              {migrating ? 'Migrando...' : 'Blindar Biblioteca'}
            </Button>
            
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => { resetForm(); setDialogOpen(true); }}
              data-testid="new-exercise-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('newExercise')}
            </Button>
          </div>
        </div>

        {/* Muscle Group Filters */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="h-4 w-4" />
            <span>Filtrar por grupo muscular:</span>
          </div>
          <ScrollArea className="w-full">
            <div className="flex gap-2 pb-2">
              {MUSCLE_GROUPS.map((group) => (
                <Button
                  key={group.key}
                  variant={selectedGroup === group.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedGroup(group.key)}
                  className={`whitespace-nowrap ${
                    selectedGroup === group.key 
                      ? `${group.color} text-white border-transparent` 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`filter-${group.key}`}
                >
                  {group.label}
                  <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                    selectedGroup === group.key ? 'bg-white/20' : 'bg-muted'
                  }`}>
                    {groupCounts[group.key] || 0}
                  </span>
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
            data-testid="exercise-search-input"
          />
          {search && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
              onClick={() => setSearch('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Exercises Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredExercises.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Dumbbell className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {search || selectedGroup !== 'all' 
                  ? 'No se encontraron ejercicios con esos filtros' 
                  : t('noExercises')}
              </p>
              {(search || selectedGroup !== 'all') && (
                <Button 
                  variant="link" 
                  onClick={() => { setSearch(''); setSelectedGroup('all'); }}
                  className="mt-2"
                >
                  Limpiar filtros
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredExercises.map((exercise) => {
              const group = getMuscleGroup(exercise);
              return (
                <Card 
                  key={exercise.id} 
                  className="bg-card border-border card-hover"
                  data-testid={`exercise-card-${exercise.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-sm ${group.color}/20`}>
                          <Dumbbell className={`h-5 w-5 ${group.color.replace('bg-', 'text-')}`} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-foreground truncate">{exercise.name}</h3>
                          <span className={`inline-block px-2 py-0.5 text-xs rounded-sm mt-1 ${group.color} text-white`}>
                            {group.label}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(exercise)}
                          className="h-8 w-8"
                          data-testid={`edit-exercise-${exercise.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setSelectedExercise(exercise); setDeleteDialogOpen(true); }}
                          className="h-8 w-8 hover:text-destructive"
                          data-testid={`delete-exercise-${exercise.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Agonista/Antagonista */}
                    {exercise.agonista_antagonista && (
                      <p className="text-xs text-muted-foreground mb-1">
                        <span className="text-primary">+</span> {exercise.agonista_antagonista}
                      </p>
                    )}
                    
                    {/* Nota clave */}
                    {exercise.nota_clave && (
                      <p className="text-sm text-amber-400/90 italic mb-2 line-clamp-2">
                        💡 {exercise.nota_clave}
                      </p>
                    )}
                    
                    {/* Legacy notes fallback */}
                    {!exercise.nota_clave && exercise.notes && (
                      <p className="text-sm text-muted-foreground italic mb-2 line-clamp-2">
                        {exercise.notes}
                      </p>
                    )}
                    
                    {exercise.video_url && (
                      <div className="mt-3">
                        {hasLocalVideo(exercise) ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 text-sm text-green-500">
                              <CheckCircle className="h-4 w-4" />
                              Video privado
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => playVideo(exercise)}
                            >
                              <Play className="h-3 w-3 mr-1" /> Ver
                            </Button>
                          </div>
                        ) : exercise.video_url.endsWith('.gif') ? (
                          <img 
                            src={exercise.video_url} 
                            alt={`Animación ${exercise.name}`}
                            className="w-full h-32 object-contain bg-black/30 rounded-sm"
                          />
                        ) : (
                          <a 
                            href={exercise.video_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            <Video className="h-4 w-4" />
                            Ver video (externo)
                          </a>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedExercise ? t('editExercise') : t('newExercise')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitWithVideo} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('exerciseName')}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="exercise-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Músculo Principal</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ej: Pectoral mayor, Dorsal ancho..."
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="exercise-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nota Clave</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Indicaciones técnicas importantes..."
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="exercise-notes-input"
                />
              </div>
              
              {/* Video Upload Section */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Video del Ejercicio
                </Label>
                
                {/* Current video display */}
                {selectedExercise && hasLocalVideo(selectedExercise) && (
                  <div className="bg-zinc-900/50 rounded-lg p-3 border border-zinc-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-500">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm">Video privado subido</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(getVideoUrl(selectedExercise), '_blank')}
                        >
                          <Play className="h-3 w-3 mr-1" /> Ver
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteVideo(selectedExercise.id)}
                        >
                          <Trash className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Upload new video */}
                <div className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoSelect}
                    className="hidden"
                    data-testid="video-file-input"
                  />
                  
                  {videoFile ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <Video className="h-5 w-5" />
                        <span className="font-medium">{videoFile.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setVideoFile(null)}
                      >
                        <X className="h-4 w-4 mr-1" /> Quitar
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Haz clic para subir un video
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        MP4, MOV, WebM o AVI (máx. 200MB)
                      </p>
                    </div>
                  )}
                  
                  {convertingVideo && (
                    <div className="mt-3">
                      <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-amber-500 h-2 rounded-full transition-all"
                          style={{ width: `${conversionProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Convirtiendo a formato web... {conversionProgress}%
                      </p>
                    </div>
                  )}

                  {uploadingVideo && !convertingVideo && (
                    <div className="mt-3">
                      <div className="w-full bg-zinc-800 rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Subiendo... {uploadProgress}%
                      </p>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  🔒 Los videos subidos son privados y solo visibles para usuarios logueados
                </p>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="bg-primary text-primary-foreground"
                  disabled={submitting || uploadingVideo || convertingVideo}
                  data-testid="save-exercise-button"
                >
                  {submitting || uploadingVideo || convertingVideo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t('save')
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteExercise')}</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedExercise?.name}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground"
                data-testid="confirm-delete-exercise"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Video Player Modal */}
        <Dialog open={videoModalOpen} onOpenChange={closeVideoModal}>
          <DialogContent className="bg-card border-border max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="h-5 w-5 text-primary" />
                {videoToPlay?.name || 'Video'}
              </DialogTitle>
            </DialogHeader>
            <div className="relative bg-black rounded-lg overflow-hidden min-h-[300px] flex items-center justify-center">
              {loadingVideo ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-muted-foreground">Cargando video...</p>
                </div>
              ) : videoBlob ? (
                <>
                  <video 
                    controls 
                    autoPlay
                    playsInline
                    preload="auto"
                    className="w-full max-h-[60vh]"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  >
                    <source src={videoBlob} type="video/mp4; codecs=hvc1" />
                    <source src={videoBlob} type="video/mp4" />
                    <source src={videoBlob} type="video/quicktime" />
                  </video>
                  <div style={{display: 'none'}} className="flex-col items-center justify-center py-12 gap-4">
                    <p className="text-muted-foreground text-center">Tu navegador no soporta este formato. Abre directamente:</p>
                    <a 
                      href={videoBlob} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-primary text-white rounded-lg font-medium"
                    >
                      Abrir Video
                    </a>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">No se pudo cargar el video</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeVideoModal}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ExercisesPage;
